import React, { createContext, Suspense, useContext, useEffect, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import tz from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
dayjs.extend(utc);
dayjs.extend(tz);

import { useFetch } from 'src/lib/FetchContext';
import { LoginContext } from 'src/lib/LoginContext';
import { usePrefetchSuspense } from './usePrefetch';
import { Spinner } from '@riac/design-system';

// NOTICE:  The following types are based on returns from the database
// and may become inaccurate over time.

export type AuctionSession = {
  AuctionSessionID: number,
  Date: string,
  StartingLot: number,
  EndingLot: number,
  AuctionSessionStatus: number,
  AuctionFinal: number,
};


// This type is a combination of data returned from the database
// as well as data appended in fetchActiveAuctions()
export type ActiveAuction = {
  AuctionID: number,
  AuctionType: string,
  Title: string,
  StartDate: string,
  EndDate: string,
  AuctionStatusID: number,
  PricesRealized: number,
  CheckOut: number,
  AuctionTypeID: number,
  LocCity: string,
  LocState: string,
  sessions: AuctionSession[],
  Slug: string,
  TextArea: string,
  FinalConsignmentDate: string | null,
  SealedBidPDFURL: string,
  PricesRealizedPDFURL: string,
  CatalogSearchLink: number,
  PhoneAvailability: number,
  AuctionClosed: number,
  StartTime: string,
  EndTime: string,
  Excerpt: string,
  CorrectionsPDFURL: string | null,
  FlipBookData: string,
  LocationID: number,
  CloseRate: number,
  ExtensionTime: number,
  LocDescription: string,
  LocAddress: string,
  LocZip: string,
  LocationEnabled: {
    type: string,
    data: number[],
  },
  active: boolean,
  approvalSent?: boolean,
  preRegistered?: boolean,
  LiveBidderStatus?: number,
};

export type GlobalDataContextValue = {
  refreshActiveAuctions: () => Promise<ActiveAuction[]>,
  refreshUpcomingAuctions: () => Promise<ActiveAuction[]>,
  upcomingAuctions: ActiveAuction[],
  activeAuctions: ActiveAuction[],
};

export const GlobalDataContext = createContext<GlobalDataContextValue>({});

type CachedData<T = any> = [cacheDateTime: Dayjs | null, data: T];

export const GlobalDataContextProvider = ({ children }): JSX.Element => {
  const { fetch } = useFetch();
  const { isLoggedIn } = useContext(LoginContext);

  const fetchActiveAuctions = async (): Promise<ActiveAuction[]> => {
    const activeAuctionsResp = await fetch('/api/auction/activeAuctions', {
      credentials: 'include',
    });

    const auctions: ActiveAuction[] = await activeAuctionsResp.json();
    await Promise.all(auctions.map(async (auction) => {
      const resp = await fetch(`/api/auction/session/${auction.AuctionID}`, {
        credentials: 'include',
      });
      const respJson: AuctionSession[] = await resp.json();
      auction.sessions = respJson;

      const mgmtResponse = await fetch(`/api/auction/auctionByID/${auction.AuctionID}`);
      const mgmtJson = await mgmtResponse.json();
      Object.assign(auction, mgmtJson[0]);

      if(isLoggedIn) {
        const [bidderStatusInfo, preRegisterInfo] = await Promise.all([
          fetch(`/api/account/info/getBidderStatus/${auction.AuctionID}`, {
            credentials: 'include',
          }).then((response) => response.json()),
          fetch(`/api/account/info/getPreRegistrationStatus/${auction.AuctionID}`, {
            credentials: 'include',
          }).then((response) => response.json()),
        ]);

        Object.assign(
          auction,
          bidderStatusInfo[0],
          { approvalSent: false }, // STUB: does this actually get used anywhere?
          { preRegistered: preRegisterInfo.response || false }
        );
      }

      if(auction.sessions.find((x) => x.AuctionSessionStatus === 3)) {
        auction.active = true;
      } else {
        auction.active = false;
      }
    }));

    return auctions;
  };

  const fetchUpcomingAuctions = async (pfActiveAuctions?: ActiveAuction[]): Promise<ActiveAuction[]> => {
    const auctions = pfActiveAuctions || await fetchActiveAuctions();

    // If no active premier auction try to add next one
    if(!auctions.find((auc) => auc.AuctionTypeID === 1)) {
      const nextAuctionsReq = await fetch('/api/auction/getNextOfType');
      const nextAuctions = await nextAuctionsReq.json();
      const nextPremier = nextAuctions.find((auc) => auc.AuctionTypeID === 1);
      if(nextPremier) {
        const mgmtResponse = await fetch(`/api/auction/auctionByID/${nextPremier.AuctionID}`);
        const mgmtJson = await mgmtResponse.json();
        Object.assign(nextPremier, mgmtJson[0]);
        auctions.push(nextPremier);
      }
    }

    const nextPremier = auctions.find((auc) => auc.AuctionTypeID === 1);
    const nextSC = auctions.find((auc) => auc.AuctionTypeID === 2);
    // eslint-disable-next-line max-len
    const upToTwoOtherAuctions = auctions.filter((auc) => auc.AuctionTypeID !== 1 && auc.AuctionTypeID !== 2).slice(0, 2);
    const newUpcomingAuctions = [nextPremier, nextSC, ...upToTwoOtherAuctions].filter((x) => x)
      .sort((a, b) => dayjs.tz(a.StartDate, 'America/Chicago').diff(dayjs.tz(b.StartDate, 'America/Chicago')));
      // DayJS Note: Start date is a string YYYY-MM-DD in Chicago time.

    return newUpcomingAuctions;
  };

  const ssrPrefetchData = usePrefetchSuspense<any>('GlobalDataContext', async () => {
    const pfActiveAuctions = await fetchActiveAuctions();
    const pfUpcomingAuctions = await fetchUpcomingAuctions(pfActiveAuctions);

    return {
      upcomingAuctions: pfUpcomingAuctions,
      activeAuctions: pfActiveAuctions,
    };
  }).read();

  //DayJS Note: DayJS is used as a client side relative time identifier here, so timezone is irrelevant.
  const [activeAuctions, setActiveAuctions] = useState<CachedData<ActiveAuction[]>>([dayjs(), ssrPrefetchData.pfActiveAuctions]);
  const [upcomingAuctions, setUpcomingAuctions] = useState<CachedData<ActiveAuction[]>>([dayjs(), ssrPrefetchData.upcomingAuctions]);

  const refreshActiveAuctions = async (): Promise<ActiveAuction[]> => {
    const newActiveAuctions = await fetchActiveAuctions();
    setActiveAuctions([dayjs(), newActiveAuctions]);
    return structuredClone(newActiveAuctions);
  };

  const refreshUpcomingAuctions = async (newActiveAuctions?: ActiveAuction[]): Promise<ActiveAuction[]> => {
    const newUpcomingAuctions = await fetchUpcomingAuctions(newActiveAuctions);
    setUpcomingAuctions([dayjs(), newUpcomingAuctions]);
    return structuredClone(newUpcomingAuctions);
  };

  const [staleTimer, setStaleTimer] = useState<NodeJS.Timeout>();

  useEffect(() => {
    if(!staleTimer) {
      const t = setTimeout(async () => {
        const newActiveAuctions = await refreshActiveAuctions();
        refreshUpcomingAuctions(newActiveAuctions);
        setStaleTimer(null);
      }, 1000 * 60 * 15);

      setStaleTimer(t);
    }

    return () => {
      clearTimeout(staleTimer);
    }
  }, [
    staleTimer,
  ]);

  return (
    <Suspense fallback = {<Spinner/>}>
      <GlobalDataContext.Provider
        value = {{
          refreshActiveAuctions,
          refreshUpcomingAuctions,
          upcomingAuctions: upcomingAuctions[1] || ssrPrefetchData.upcomingAuctions,
          activeAuctions: activeAuctions[1] || ssrPrefetchData.activeAuctions,
        }}
      >
        {children}
      </GlobalDataContext.Provider>
    </Suspense>
  );
};

